// simulation.ts — Lobby simulation system with deterministic exponential growth curves

import type { LobbyMetadata } from "@game/domain";

export interface LobbySimulation {
  realPlayerCount: number;
  realMaxPlayers: number;
  simulatedPlayerCount: number;
  simulatedMaxPlayers: number;
  simulatedSecondsUntilStart: number;
  lastUpdateTime: number;
  countdownStartTime: number;
  countdownStartSeconds: number;
  targetCount: number;
  hasInitialSpike: boolean;
  isRapidCountUp: boolean;
  rapidTargetCount: number;
}

export interface SimulationConfig {
  maxSimulatedPlayers: number;
  totalLobbyTime: number;
  exponentialSteepness: number;
  minStartingPlayers: number;
  jitterRange: number;
  updateInterval: number;
  minUpdateInterval: number;
}

const DEFAULT_SIMULATION_CONFIG: SimulationConfig = {
  maxSimulatedPlayers: 100,
  totalLobbyTime: 15,
  exponentialSteepness: 4,
  minStartingPlayers: 30,
  jitterRange: 2,
  updateInterval: 200,
  minUpdateInterval: 100,
};

const lobbySimulations = new Map<string, LobbySimulation>();
let simulationUpdateTimer: ReturnType<typeof setInterval> | null = null;
let simulationConfig = DEFAULT_SIMULATION_CONFIG;

export function configureSimulation(config: Partial<SimulationConfig>): void {
  simulationConfig = { ...DEFAULT_SIMULATION_CONFIG, ...config };
}

export function generateSimulatedLobbyData(
  _realCount: number,
  _realMax: number,
  isFirstLobby: boolean,
): { simMax: number; simCount: number } {
  const simMax = simulationConfig.maxSimulatedPlayers;

  if (!isFirstLobby) {
    return { simMax, simCount: 0 };
  }

  return { simMax, simCount: 94 };
}

export function calculateExponentialProgress(
  timeProgress: number,
  steepness: number = simulationConfig.exponentialSteepness,
): number {
  return Math.max(0, Math.min(1, 1 - Math.exp(-steepness * timeProgress)));
}

export function calculateDeterministicJitter(
  lobbyId: string,
  timeRemaining: number,
  jitterRange: number = simulationConfig.jitterRange,
): number {
  let hash = 0;
  for (let i = 0; i < lobbyId.length; i++) {
    hash = (hash << 5) - hash + lobbyId.charCodeAt(i);
    hash = hash & hash;
  }

  const timeScaled = Math.floor(timeRemaining * 10);
  const combinedSeed = (hash + timeScaled) ^ (timeScaled << 3);
  const absoluteSeed = Math.abs(combinedSeed);

  const normalized = (absoluteSeed % (jitterRange * 2 + 1)) - jitterRange;
  return normalized;
}

export function calculateSimulatedPlayerCount(
  lobby: LobbyMetadata,
  lobbyId: string,
): number {
  const { totalLobbyTime, minStartingPlayers } = simulationConfig;
  const timeRemaining = lobby.secondsUntilStart;

  const progress = Math.max(
    0,
    Math.min(1, (totalLobbyTime - timeRemaining) / totalLobbyTime),
  );

  const exponentialProgress = calculateExponentialProgress(progress);

  const maxPlayers = 94 + Math.floor(lobbyId.charCodeAt(0) % 5);
  const curveBasedCount = Math.floor(
    minStartingPlayers +
      (maxPlayers - minStartingPlayers) * exponentialProgress,
  );

  const jitter = calculateDeterministicJitter(lobbyId, timeRemaining);
  const finalCount = Math.max(
    lobby.playerCount,
    minStartingPlayers,
    Math.min(simulationConfig.maxSimulatedPlayers, curveBasedCount + jitter),
  );

  return finalCount;
}

export function updateLobbySimulation(
  lobbyId: string,
  realCount: number,
  realMax: number,
  isFirstLobby: boolean,
  lobbiesList: LobbyMetadata[],
): LobbySimulation {
  const now = Date.now();
  const lobby = lobbiesList.find((l) => l.id === lobbyId);
  const initialCountdownSeconds = Math.max(
    1,
    Math.floor(lobby?.secondsUntilStart ?? 10),
  );
  let sim = lobbySimulations.get(lobbyId);

  if (!sim) {
    const { simMax, simCount } = generateSimulatedLobbyData(
      realCount,
      realMax,
      isFirstLobby,
    );
    sim = {
      realPlayerCount: realCount,
      realMaxPlayers: realMax,
      simulatedPlayerCount: Math.max(realCount, isFirstLobby ? 0 : 0),
      simulatedMaxPlayers: simMax,
      simulatedSecondsUntilStart: isFirstLobby ? initialCountdownSeconds : 0,
      lastUpdateTime: now,
      countdownStartTime: now,
      countdownStartSeconds: initialCountdownSeconds,
      targetCount: simCount,
      hasInitialSpike: false,
      isRapidCountUp: false,
      rapidTargetCount: 0,
    };
    lobbySimulations.set(lobbyId, sim);
  }

  sim.realPlayerCount = realCount;
  sim.realMaxPlayers = realMax;

  if (isFirstLobby) {
    if (lobby) {
      const incomingSeconds = Math.max(1, Math.floor(lobby.secondsUntilStart));

      if (incomingSeconds > sim.countdownStartSeconds) {
        sim.countdownStartSeconds = incomingSeconds;
        sim.countdownStartTime = now;
      }

      const elapsedSeconds = Math.floor((now - sim.countdownStartTime) / 1000);
      const cycleLength = sim.countdownStartSeconds + 1;
      const cycleOffset = elapsedSeconds % cycleLength;
      sim.simulatedSecondsUntilStart = Math.max(
        0,
        sim.countdownStartSeconds - cycleOffset,
      );

      sim.simulatedPlayerCount = calculateSimulatedPlayerCount(
        {
          ...lobby,
          secondsUntilStart: sim.simulatedSecondsUntilStart,
        },
        lobbyId,
      );
    } else {
      sim.simulatedSecondsUntilStart = 0;
      sim.simulatedPlayerCount = realCount;
    }
  } else {
    sim.simulatedSecondsUntilStart = lobby?.secondsUntilStart ?? 0;
    sim.simulatedPlayerCount = 0;
  }

  sim.lastUpdateTime = now;

  return sim;
}

export function startLobbySimulationUpdates(
  updateLobbyCardFn: (
    card: HTMLElement,
    lobby: LobbyMetadata,
    isFirst: boolean,
  ) => void,
  getLobbyListFn: () => LobbyMetadata[],
): void {
  if (simulationUpdateTimer) return;

  simulationUpdateTimer = setInterval(() => {
    const lobbyBrowserEl = document.getElementById("lobbyBrowser");
    if (!lobbyBrowserEl || lobbyBrowserEl.style.display === "none") return;

    const lobbyListEl = document.getElementById("lobbyList");
    if (!lobbyListEl) return;

    let needsUpdate = false;

    for (const [_lobbyId, sim] of lobbySimulations.entries()) {
      const timeSinceUpdate = Date.now() - sim.lastUpdateTime;
      if (timeSinceUpdate > simulationConfig.minUpdateInterval) {
        needsUpdate = true;
        break;
      }
    }

    const lobbiesList = getLobbyListFn();
    if (needsUpdate && lobbiesList.length > 0) {
      const cards = lobbyListEl.querySelectorAll(".lobby-card");
      cards.forEach((card, index) => {
        const lobby = lobbiesList[index];
        if (lobby && card instanceof HTMLElement) {
          updateLobbyCardFn(card, lobby, index === 0);
        }
      });
    }

    const currentLobbyIds = new Set(lobbiesList.map((l) => l.id));
    for (const lobbyId of lobbySimulations.keys()) {
      if (!currentLobbyIds.has(lobbyId)) {
        lobbySimulations.delete(lobbyId);
      }
    }
  }, simulationConfig.updateInterval);
}

export function stopLobbySimulationUpdates(): void {
  if (simulationUpdateTimer) {
    clearInterval(simulationUpdateTimer);
    simulationUpdateTimer = null;
  }
}

export function getLobbySimulation(lobbyId: string): LobbySimulation | null {
  return lobbySimulations.get(lobbyId) || null;
}

export function getAllLobbySimulations(): Map<string, LobbySimulation> {
  return new Map(lobbySimulations);
}

export function clearAllLobbySimulations(): void {
  lobbySimulations.clear();
}

export function removeLobbySimulation(lobbyId: string): boolean {
  return lobbySimulations.delete(lobbyId);
}

export function getSimulationStats(): {
  activeSimulations: number;
  isTimerRunning: boolean;
  config: SimulationConfig;
} {
  return {
    activeSimulations: lobbySimulations.size,
    isTimerRunning: simulationUpdateTimer !== null,
    config: { ...simulationConfig },
  };
}
